/**
 * Plan-required (403) normalizer — S8, security I0.
 *
 * Every service renders the shared plan-feature gate
 * (`pubsocialnet_common.entitlements.require_plan_feature`) as
 *   403 {error: "feature_not_in_plan", feature, plan_code, upgrade_url, detail}
 * (`plan_required` is accepted as an alias). The API client (lib/api/client.ts)
 * recognises that body on ANY call, fires the `psn:plan-required` window event,
 * then still throws the ApiError so the call site's own handling runs.
 *
 * Listeners:
 *  - the entitlements store marks the feature as server-denied, so every
 *    `<PlanGate feature=…>` switches to the locked/upgrade card (the server is
 *    authoritative even when the cached plan document said "included");
 *  - `PlanRequiredHost` (shell) shows the upgrade dialog once per feature.
 */

import { tr } from '@/i18n';

export const PLAN_REQUIRED_EVENT = 'psn:plan-required';

export const PLAN_REQUIRED_ERRORS: ReadonlySet<string> = new Set(['feature_not_in_plan', 'plan_required']);

export interface PlanRequired {
  /** Plan feature key the server refused (e.g. `advanced_analytics`). */
  feature: string;
  /** Tenant's resolved plan code (null when unresolved). */
  planCode: string | null;
  /** Where to upgrade (server-provided, defaults to the subscription page). */
  upgradeUrl: string;
  /** Server message (French by default). */
  detail: string | null;
}

const DEFAULT_UPGRADE_URL = '/settings/subscription';

/** Normalize a (status, body) pair into a PlanRequired, or null for anything else. */
export function parsePlanRequired(status: number, body: unknown): PlanRequired | null {
  if (status !== 403) return null;
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  // FastAPI HTTPException shape: {detail: {error, feature, …}}
  const inner =
    b.detail && typeof b.detail === 'object' && !Array.isArray(b.detail)
      ? (b.detail as Record<string, unknown>)
      : b;
  const error = typeof inner.error === 'string' ? inner.error : null;
  if (!error || !PLAN_REQUIRED_ERRORS.has(error)) return null;
  const feature = typeof inner.feature === 'string' && inner.feature ? inner.feature : 'unknown';
  const planCode = typeof inner.plan_code === 'string' ? inner.plan_code : null;
  const upgradeUrl =
    typeof inner.upgrade_url === 'string' && inner.upgrade_url.startsWith('/')
      ? inner.upgrade_url
      : DEFAULT_UPGRADE_URL;
  const detail = typeof inner.detail === 'string' ? inner.detail : null;
  return { feature, planCode, upgradeUrl, detail };
}

/** PlanRequired from a caught error (ApiError-like: {status, body}), else null. */
export function planRequiredOf(err: unknown): PlanRequired | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as { status?: unknown; body?: unknown };
  if (typeof e.status !== 'number') return null;
  return parsePlanRequired(e.status, e.body);
}

export function isPlanRequiredError(err: unknown): boolean {
  return planRequiredOf(err) !== null;
}

export function emitPlanRequired(payload: PlanRequired): void {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  try {
    window.dispatchEvent(new CustomEvent<PlanRequired>(PLAN_REQUIRED_EVENT, { detail: payload }));
  } catch {
    /* CustomEvent unavailable — the thrown ApiError still surfaces at the call site */
  }
}

/** French inline message for a call site that wants to name the real cause. */
export function planRequiredText(info: PlanRequired, label?: string): string {
  const name = label ?? info.feature.replace(/[_.-]+/g, ' ');
  return tr('shell.lib.apiPlanGate.nameIsNotIncludedInYour', { name });
}
